// src/api/finance/FinanceApi.ts
import { ENV } from '../../config/runtimeEnv';
import {
	Payment,
	Invoice,
	InvoicePayment,
	Transaction,
	CreatePaymentRequest,
	CreateInvoiceRequest,
	UpdateInvoiceRequest,
	RecordInvoicePaymentRequest,
	InvoiceListParams,
	InvoiceListEnvelope,
	IssueInvoiceResult,
	PaymentLedgerResult,
	ClinicSettings,
	UpdateClinicSettingsRequest,
	RefundRequest,
	StripeCustomer,
	CreateFinanceCustomerRequest,
	StripePaymentMethod,
	AttachPaymentMethodRequest,
	SetDefaultPaymentMethodRequest,
	CreateSubscriptionRequest,
	UpdateSubscriptionRequest,
	CancelSubscriptionRequest,
	Subscription,
	SubscriptionResponse,
	CompanyDashboardStats,
	UserDashboardStats,
	ApiResponse,
	PaginatedResponse,
} from "../../types/finance/FinanceTypes";
import { User } from "../../types/admin/UserTypes";
import type { UserUuid } from "../../types/identity";
import { http } from "../../lib/http/client";

const FINANCE_BASE_URL = ENV.VITE_FINANCE_SERVER || "/api/finance";

// Get auth headers
const getHeaders = () => ({
	"Content-Type": "application/json",
});

// The anonymous booking flow's capability (contract §16.5): minted with the customer, held in memory for that booking, sent only on the three public payment routes.
export const BOOKING_PAYMENT_TOKEN_HEADER = "X-Booking-Payment-Token";

const bookingPaymentHeaders = (paymentToken?: string): Record<string, string> =>
	paymentToken ? { ...getHeaders(), [BOOKING_PAYMENT_TOKEN_HEADER]: paymentToken } : getHeaders();

export interface PublicFinanceCustomer {
	customer: StripeCustomer;
	paymentToken: string;
	paymentTokenExpiresAt: string;
}

interface PublicCustomerResponse {
	success: boolean;
	data: StripeCustomer;
	payment_token: string;
	payment_token_expires_at: string;
}

// ==================== DASHBOARD ====================

export const fetchCompanyDashboard = async (): Promise<CompanyDashboardStats> => {
	const response = await http.get<ApiResponse<CompanyDashboardStats>>(
		`${FINANCE_BASE_URL}/dashboard/clinic`,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

export const fetchUserDashboard = async (userId: string): Promise<UserDashboardStats> => {
	const response = await http.get<ApiResponse<UserDashboardStats>>(
		`${FINANCE_BASE_URL}/dashboard/user/${userId}`,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

// ==================== CUSTOMER MANAGEMENT ====================

export const createFinanceCustomer = async (data: CreateFinanceCustomerRequest): Promise<User> => {
	const response = await http.post<ApiResponse<User>>(
		`${FINANCE_BASE_URL}/customers`,
		data,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

// Always a new Stripe customer; the user it belongs to stays server-side, bound to the token.
export const createFinanceCustomerPublic = async (data: CreateFinanceCustomerRequest): Promise<PublicFinanceCustomer> => {
	const response = await http.post<PublicCustomerResponse>(
		`${FINANCE_BASE_URL}/customers/public`,
		data,
		{ headers: getHeaders() }
	);
	return {
		customer: response.data.data,
		paymentToken: response.data.payment_token,
		paymentTokenExpiresAt: response.data.payment_token_expires_at,
	};
};

export const getFinanceCustomer = async (userId: UserUuid): Promise<User> => {
	const response = await http.get<ApiResponse<User>>(
		`${FINANCE_BASE_URL}/customers/${userId}`,
		{
			headers: getHeaders(),
		}
	);
	return response.data.data;
};

export const deleteFinanceCustomer = async (userId: UserUuid): Promise<void> => {
	await http.delete(`${FINANCE_BASE_URL}/customers/${userId}`, {
		headers: getHeaders(),
	});
};

// ==================== STRIPE PAYMENT METHODS ====================

export const attachPaymentMethod = async (data: AttachPaymentMethodRequest): Promise<StripePaymentMethod> => {
	const response = await http.post<ApiResponse<StripePaymentMethod>>(
		`${FINANCE_BASE_URL}/stripe/payment-methods/attach`,
		data,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

export const attachPaymentMethodPublic = async (data: AttachPaymentMethodRequest, paymentToken: string): Promise<StripePaymentMethod> => {
	const response = await http.post<ApiResponse<StripePaymentMethod>>(
		`${FINANCE_BASE_URL}/stripe/payment-methods/attach/public`,
		data,
		{ headers: bookingPaymentHeaders(paymentToken) }
	);
	return response.data.data;
};

export const detachPaymentMethod = async (paymentMethodId: string): Promise<StripePaymentMethod> => {
	const response = await http.delete<ApiResponse<StripePaymentMethod>>(
		`${FINANCE_BASE_URL}/stripe/payment-methods/${paymentMethodId}`,
		{
			headers: getHeaders(),
		}
	);
	return response.data.data;
};

export const setDefaultPaymentMethod = async (data: SetDefaultPaymentMethodRequest): Promise<StripeCustomer> => {
	const response = await http.post<ApiResponse<StripeCustomer>>(
		`${FINANCE_BASE_URL}/stripe/payment-methods/set-default`,
		data,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

export const listPaymentMethods = async (customerId: string): Promise<StripePaymentMethod[]> => {
	const response = await http.get<ApiResponse<StripePaymentMethod[]>>(
		`${FINANCE_BASE_URL}/stripe/payment-methods/customer/${customerId}`,
		{
			headers: getHeaders(),
		}
	);
	return response.data.data;
};

export const listPaymentMethodsPublic = async (customerId: string, paymentToken: string): Promise<StripePaymentMethod[]> => {
	const response = await http.get<ApiResponse<StripePaymentMethod[]>>(
		`${FINANCE_BASE_URL}/stripe/payment-methods/customer/${customerId}/public`,
		{
			headers: bookingPaymentHeaders(paymentToken),
		}
	);
	return response.data.data;
};

// ==================== PAYMENTS ====================

export const fetchPayments = async (
	page: number = 1,
	perPage: number = 20
): Promise<PaginatedResponse<Payment>> => {
	const response = await http.get(`${FINANCE_BASE_URL}/payments/clinic`, {
		headers: getHeaders(),
		params: { page, per_page: perPage },
	});

	return {
		data: response.data.data || response.data,
		total: response.data.total || response.data.length,
		page,
		perPage,
	};
};

export const fetchPaymentsByUser = async (userId: string): Promise<Payment[]> => {
	const response = await http.get(`${FINANCE_BASE_URL}/payments/user/${userId}`, {
		headers: getHeaders(),
	});
	return response.data.data || response.data;
};

export const fetchPayment = async (id: string): Promise<Payment> => {
	const response = await http.get(`${FINANCE_BASE_URL}/payments/${id}`, {
		headers: getHeaders(),
	});
	return response.data;
};

export const createPayment = async (data: CreatePaymentRequest): Promise<Payment> => {
	const response = await http.post(`${FINANCE_BASE_URL}/payments`, data, {
		headers: getHeaders(),
	});
	return response.data;
};

// With a booking token finance takes the user from the token, so `userId` is omitted; a signed-in caller pays as itself.
export const createPaymentPublic = async (
	data: Omit<CreatePaymentRequest, "userId"> & { userId?: string },
	paymentToken?: string
): Promise<Payment> => {
	const response = await http.post(`${FINANCE_BASE_URL}/payments/public`, data, {
		headers: bookingPaymentHeaders(paymentToken),
	});
	return response.data;
};

export const refundPayment = async (id: string, data: RefundRequest): Promise<Payment> => {
	const response = await http.post(`${FINANCE_BASE_URL}/payments/${id}/refund`, data, {
		headers: getHeaders(),
	});
	return response.data;
};

// ==================== SUBSCRIPTIONS ====================

export const createSubscription = async (data: CreateSubscriptionRequest): Promise<SubscriptionResponse> => {
	const response = await http.post<ApiResponse<SubscriptionResponse>>(
		`${FINANCE_BASE_URL}/subscriptions`,
		data,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

export const getSubscription = async (subscriptionId: string): Promise<SubscriptionResponse> => {
	const response = await http.get<ApiResponse<SubscriptionResponse>>(
		`${FINANCE_BASE_URL}/subscriptions/${subscriptionId}`,
		{
			headers: getHeaders(),
		}
	);
	return response.data.data;
};

export const updateSubscription = async (
	subscriptionId: string,
	data: UpdateSubscriptionRequest
): Promise<SubscriptionResponse> => {
	const response = await http.put<ApiResponse<SubscriptionResponse>>(
		`${FINANCE_BASE_URL}/subscriptions/${subscriptionId}`,
		data,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

export const cancelSubscription = async (
	subscriptionId: string,
	data: CancelSubscriptionRequest
): Promise<SubscriptionResponse> => {
	const response = await http.delete<ApiResponse<SubscriptionResponse>>(
		`${FINANCE_BASE_URL}/subscriptions/${subscriptionId}`,
		{
			headers: getHeaders(),
			data: data,
		}
	);
	return response.data.data;
};

export const fetchSubscriptionsByUser = async (userId: string): Promise<Subscription[]> => {
	const response = await http.get<ApiResponse<Subscription[]>>(
		`${FINANCE_BASE_URL}/subscriptions/user/${userId}`,
		{
			headers: getHeaders(),
		}
	);
	return response.data.data;
};

export const fetchSubscriptionsByCompany = async (): Promise<Subscription[]> => {
	const response = await http.get<ApiResponse<Subscription[]>>(
		`${FINANCE_BASE_URL}/subscriptions/clinic`,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

// ==================== INVOICES ====================

export const createInvoice = async (data: CreateInvoiceRequest): Promise<Invoice> => {
	const response = await http.post<ApiResponse<Invoice>>(
		`${FINANCE_BASE_URL}/invoices`,
		data,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

export const updateInvoice = async (id: string, data: UpdateInvoiceRequest): Promise<Invoice> => {
	const response = await http.put<ApiResponse<Invoice>>(
		`${FINANCE_BASE_URL}/invoices/${id}`,
		data,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

/**
 * **Issue the invoice** — the single transition out of draft. Allocates the number, freezes the
 * clinic snapshot and, for a `collectViaStripe` invoice, builds the Stripe mirror.
 *
 * ⚠️ **`warnings` is on the TOP LEVEL of the envelope, not inside `data`.** A response carrying
 * `stripe_mirror_pending` is a SUCCESS: the invoice is issued and only its payment page is
 * missing. Reading warnings off `response.data.data` silently loses them.
 */
export const issueInvoice = async (id: string): Promise<IssueInvoiceResult> => {
	const response = await http.post<
		ApiResponse<{ invoice: Invoice; stripeInvoice: IssueInvoiceResult["stripeInvoice"] }>
	>(`${FINANCE_BASE_URL}/invoices/${id}/issue`, {}, { headers: getHeaders() });

	return {
		invoice: response.data.data.invoice,
		stripeInvoice: response.data.data.stripeInvoice ?? null,
		warnings: response.data.warnings ?? [],
	};
};

/**
 * **Email the invoice to its patient** — `POST /invoices/:id/send` (I7). This is the action a
 * clinic means by "send an invoice": it issues the invoice first if it is still a draft, mints or
 * reuses the pay link, enqueues the templated email through the notification outbox and schedules
 * the overdue reminders. `issueInvoice` (`POST /issue`) only issues and builds the Stripe mirror —
 * it sends NOTHING — so the send action must call this, not that.
 *
 * ⚠️ Unlike `/issue`, `POST /send` returns the invoice DIRECTLY as `data` (there is no nested
 * `stripeInvoice`); `warnings` are still on the top level, and a `stripe_mirror_pending` response
 * is a SUCCESS (the invoice was sent, only its Stripe payment page is still being built).
 */
export const sendInvoice = async (id: string): Promise<IssueInvoiceResult> => {
	const response = await http.post<ApiResponse<Invoice>>(
		`${FINANCE_BASE_URL}/invoices/${id}/send`,
		{},
		{ headers: getHeaders() }
	);

	return {
		invoice: response.data.data,
		stripeInvoice: null,
		warnings: response.data.warnings ?? [],
	};
};

export const fetchInvoices = async (
	params: InvoiceListParams = {}
): Promise<PaginatedResponse<Invoice>> => {
	// Only send the keys that are actually set. The service's global ValidationPipe runs with
	// `forbidNonWhitelisted`, and an explicit `undefined`/`""` for an enum-validated param
	// (`status`, `sort`, `order`) is a 400 rather than "no filter".
	const query: Record<string, string | number> = {};
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== "") {
			query[key] = value as string | number;
		}
	});

	const response = await http.get<InvoiceListEnvelope>(`${FINANCE_BASE_URL}/invoices/clinic`, {
		headers: getHeaders(),
		params: query,
	});

	return {
		data: response.data.data ?? [],
		total: response.data.total ?? 0,
		page: response.data.page ?? params.page ?? 1,
		perPage: response.data.per_page ?? params.per_page,
	};
};

// ==================== INVOICE PAYMENT LEDGER ====================

/** The invoice's payment ledger, newest `receivedAt` first. Reversed rows are included. */
export const fetchInvoicePayments = async (invoiceId: string): Promise<InvoicePayment[]> => {
	const response = await http.get<ApiResponse<InvoicePayment[]>>(
		`${FINANCE_BASE_URL}/invoices/${invoiceId}/payments`,
		{ headers: getHeaders() }
	);
	return response.data.data ?? [];
};

/**
 * Record money that arrived out of band. Part payments are supported.
 *
 * Known deliberate 4xx the caller must surface rather than swallow:
 *   400 — "Payment exceeds the balance due on this invoice"
 *   400 — "Stripe-collected invoices can only be settled in full out of band…"
 *   409 — the invoice is paid, cancelled or imported
 */
export const recordInvoicePayment = async (
	invoiceId: string,
	data: RecordInvoicePaymentRequest
): Promise<PaymentLedgerResult> => {
	const response = await http.post<
		ApiResponse<{ invoice: Invoice; payment: InvoicePayment }>
	>(`${FINANCE_BASE_URL}/invoices/${invoiceId}/payments`, data, { headers: getHeaders() });

	return {
		invoice: response.data.data.invoice,
		payment: response.data.data.payment,
		warnings: response.data.warnings ?? [],
	};
};

/**
 * Reverse a recorded payment — a correction, not a refund. Nothing is deleted despite the verb:
 * the payment is marked `reversed` and a compensating transaction is written.
 */
export const reverseInvoicePayment = async (
	invoiceId: string,
	paymentId: string
): Promise<PaymentLedgerResult> => {
	const response = await http.delete<
		ApiResponse<{ invoice: Invoice; payment: InvoicePayment }>
	>(`${FINANCE_BASE_URL}/invoices/${invoiceId}/payments/${paymentId}`, {
		headers: getHeaders(),
	});

	return {
		invoice: response.data.data.invoice,
		payment: response.data.data.payment,
		warnings: response.data.warnings ?? [],
	};
};

// ==================== CLINIC SETTINGS ====================

/**
 * ⚠️ The path is `/clinic-settings`, NOT `/finance/clinic-settings` — the `finance/` segment in
 * the platform-level URL belongs to the gateway, and `FINANCE_BASE_URL` already includes it.
 *
 * `data` is `null` when the clinic has never saved its settings. That is not a 404.
 */
export const fetchClinicSettings = async (): Promise<ClinicSettings | null> => {
	const response = await http.get<ApiResponse<ClinicSettings | null>>(
		`${FINANCE_BASE_URL}/clinic-settings`,
		{ headers: getHeaders() }
	);
	return response.data.data ?? null;
};

/** Merge semantics — only the keys present are written. `legalName` is required. */
export const updateClinicSettings = async (
	data: UpdateClinicSettingsRequest
): Promise<ClinicSettings> => {
	const response = await http.put<ApiResponse<ClinicSettings>>(
		`${FINANCE_BASE_URL}/clinic-settings`,
		data,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

export const fetchInvoicesByUser = async (userId: string): Promise<Invoice[]> => {
	const response = await http.get(`${FINANCE_BASE_URL}/invoices/user/${userId}`, {
		headers: getHeaders(),
	});
	return response.data.data || response.data;
};

export const fetchInvoice = async (id: string): Promise<Invoice> => {
	const response = await http.get<ApiResponse<Invoice>>(
		`${FINANCE_BASE_URL}/invoices/${id}`,
		{ headers: getHeaders() }
	);
	return response.data.data;
};

/**
 * ⚠️ `markInvoiceAsSent` and `markInvoiceAsPaid` were REMOVED in I8.
 *
 * `PUT /invoices/:id/send` still exists server-side as an older name for `issue` — it calls the
 * same service method — but having two client functions for one transition is how a caller ends
 * up on the one that drops `warnings`. Use `issueInvoice`.
 *
 * `PUT /invoices/:id/pay` flipped the status with no payment row behind it, so the invoice said
 * "paid" while its ledger said nothing had arrived. Money is recorded through
 * `recordInvoicePayment`, which writes the row, reconciles `amountPaidCents` from the ledger and
 * sets `paid`/`partially_paid` itself.
 */
export const cancelInvoice = async (id: string): Promise<Invoice> => {
	const response = await http.put<ApiResponse<Invoice>>(
		`${FINANCE_BASE_URL}/invoices/${id}/cancel`,
		{},
		{ headers: getHeaders() }
	);
	return response.data.data;
};

// ==================== TRANSACTIONS ====================

export const fetchTransactions = async (
	page: number = 1,
	perPage: number = 20
): Promise<PaginatedResponse<Transaction>> => {
	const response = await http.get(`${FINANCE_BASE_URL}/transactions/clinic`, {
		headers: getHeaders(),
		params: { page, per_page: perPage },
	});

	return {
		data: response.data.data || response.data,
		total: response.data.total || response.data.length,
		page,
		perPage,
	};
};

export const fetchTransactionsByUser = async (userId: string): Promise<Transaction[]> => {
	const response = await http.get(`${FINANCE_BASE_URL}/transactions/user/${userId}`, {
		headers: getHeaders(),
	});
	return response.data.data || response.data;
};

export const fetchTransactionsByPayment = async (paymentId: string): Promise<Transaction[]> => {
	const response = await http.get(`${FINANCE_BASE_URL}/transactions/payment/${paymentId}`, {
		headers: getHeaders(),
	});
	return response.data.data || response.data;
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Format currency amount.
 *
 * ⚠️ Accepts a string because the service's dollar-named fields ARE strings on the wire
 * (`"271.00"`) — see the header of `FinanceTypes.ts`. It still goes through `Intl`, i.e. through
 * a float, which is fine for the payment/subscription screens it serves.
 *
 * **New invoicing code must NOT use this.** A tax invoice renders from
 * `utils/finance/money.ts` — `formatDollarString` (string surgery on the dollar field) or
 * `formatCents` (integer arithmetic on the cents field) — neither of which touches a float.
 */
export const formatCurrency = (amount: number | string, currency: string = "AUD"): string => {
	return new Intl.NumberFormat("en-AU", {
		style: "currency",
		currency,
	}).format(Number(amount));
};

/**
 * Format date
 */
export const formatDate = (date: string | Date): string => {
	return new Intl.DateTimeFormat("en-AU", {
		year: "numeric",
		month: "short",
		day: "numeric",
	}).format(new Date(date));
};

/**
 * Get payment status color
 */
export const getPaymentStatusColor = (status: string): string => {
	const colors: Record<string, string> = {
		completed: "green",
		processing: "blue",
		pending: "yellow",
		failed: "red",
		refunded: "gray",
		cancelled: "gray",
	};
	return colors[status.toLowerCase()] || "gray";
};

/**
 * Get invoice status color.
 *
 * ⚠️ Feed this `invoice.effectiveStatus`, never `invoice.status` — lateness is derived
 * server-side and the stored status of a late invoice still reads `sent`.
 */
export const getInvoiceStatusColor = (status: string): string => {
	const colors: Record<string, string> = {
		paid: "green",
		partially_paid: "orange",
		sent: "blue",
		draft: "gray",
		overdue: "red",
		cancelled: "gray",
	};
	return colors[status.toLowerCase()] || "gray";
};

/**
 * Get subscription status color
 */
export const getSubscriptionStatusColor = (status: string): string => {
	const colors: Record<string, string> = {
		active: "green",
		trialing: "blue",
		past_due: "orange",
		canceled: "gray",
		cancelled: "gray",
		incomplete: "yellow",
		incomplete_expired: "red",
		unpaid: "red",
	};
	return colors[status.toLowerCase()] || "gray";
};